import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_AUTH_RETRY } from '../auth/auth-context';
import { StorageKey } from '../storage-keys.enum';
import { UserSessionStateService } from './user-session-state.service';

export interface PlgContact {
    email: string | null;
    firstname: string | null;
    lastname: string | null;
}

interface CreateSessionResponse {
    session_id: string;
    created_at: string;
    contact: PlgContact;
}

/** Pre-bearer-token builds wrote contact PII to this key. Cleaned up on init. */
const LEGACY_CONTACT_KEY = 'dr_plg_contact';

/**
 * Sentinel persisted as the plg_token when ``environment.plgAuthBypass`` is on
 * and the user lands without a real token. The value is opaque — the backend
 * bypass route never reads it; it only needs *some* non-empty string so the
 * existing ``performBootstrap()`` guard (`if (!token) requireSignUp()`) lets
 * the POST /sessions call through.
 */
const DEV_BYPASS_TOKEN = 'dev-bypass';

/**
 * Owns the HubSpot `plg_token`, the bound session id, and the "needs sign-up"
 * signal that drives the in-app landing screen.
 *
 * Flow on every cold app load:
 *  1. If the URL carries `?plg_token=…`:
 *     - If a different token is already stored, fully reset the previous
 *       contact's session/onboarding state before persisting the new token —
 *       a different `plg_token` means a different HubSpot contact and the
 *       stored `session_id` belongs to someone else.
 *     - Persist the (new) token and strip the query param.
 *  2. Do NOT mint a session at init. Bootstrap is lazy:
 *     - If a `session_id` is already stored, trust it — the first 401 from a
 *       gated call will trigger a silent re-bootstrap via the 401 interceptor.
 *     - Otherwise the wizard's "Start" → ERP-Next click drives the bootstrap
 *       through `UserSessionService.createSession()`. This keeps the Welcome
 *       step reachable for first-time users; eager bootstrapping would mint
 *       a session id that the `onboardingWelcomeGuard` reads as "returning
 *       user, skip welcome and resume the wizard".
 *  3. If no token is present at app load, do NOTHING and let the app render
 *     normally. The user can browse public screens; only when something
 *     actually needs a session (a `bootstrapSession()` call) and there is
 *     no usable token does `signUpRequired$` flip to `true`, which the
 *     shell uses to swap the routed content for the in-app landing.
 *
 * Failed bootstraps (missing token, bearer rejected, network error, second
 * 401 from the retry interceptor) all funnel through `requireSignUp()` —
 * they clear local state and surface the in-app landing rather than auto-
 * bouncing to the marketing site. The Sign Up button on the landing handles
 * the user-driven bounce.
 *
 * Contact data is in-memory only by design: on reload we either have a valid
 * session_id (no /sessions call, no contact) or we re-bootstrap (which returns
 * the contact again). Subscribers watch `contact$` so they re-render when a
 * silent re-bootstrap populates the contact mid-session.
 */
@Injectable({ providedIn: 'root' })
export class PlgTokenService {
    private readonly http = inject(HttpClient);
    private readonly state = inject(UserSessionStateService);

    private readonly _contact$ = new BehaviorSubject<PlgContact | null>(null);
    private readonly _signUpRequired$ = new BehaviorSubject<boolean>(false);
    private _bootstrapInFlight: Promise<string> | null = null;

    /** Emits the latest verified contact, or `null` when not yet known. */
    readonly contact$: Observable<PlgContact | null> = this._contact$.asObservable();

    /**
     * Emits `true` once we know the user must sign up before they can do
     * anything that needs a session. The shell shows the in-app landing in
     * place of the routed content while this is `true`.
     */
    readonly signUpRequired$: Observable<boolean> = this._signUpRequired$.asObservable();

    /** Snapshot of the latest contact (or `null`). Prefer `contact$` for templates. */
    get contact(): PlgContact | null {
        return this._contact$.value;
    }

    /** Sync snapshot of `signUpRequired$` for synchronous error-handler branching. */
    get signUpRequired(): boolean {
        return this._signUpRequired$.value;
    }

    async init(): Promise<void> {
        // One-time migration: older builds persisted contact PII under
        // `dr_plg_contact`. We now keep contact in memory only, so wipe any
        // legacy entry on every cold load. Cheap; safe to leave in place.
        localStorage.removeItem(LEGACY_CONTACT_KEY);

        const urlToken = this.readUrlToken();
        if (urlToken) {
            const storedToken = this.getToken();
            if (storedToken && storedToken !== urlToken) {
                console.info('[plg-token] token changed — resetting prior contact state');
                this.resetForNewContact();
            }
            this.persistToken(urlToken);
            this.stripUrlParam();
        } else if (environment.plgAuthBypass && !this.getToken()) {
            // Dev-only auth bypass: persist a sentinel token so the lazy
            // bootstrap path treats this browser as authenticated. The matching
            // backend ``plg__skip_hubspot_auth`` flag short-circuits HubSpot
            // verification on POST /sessions, so the value of the token is
            // irrelevant — it just needs to be non-empty. Guard with
            // ``!this.getToken()`` so a real token (from a prior ``?plg_token=…``
            // visit or a deliberate paste) is never clobbered.
            console.info('[plg-token] dev bypass — using stub token');
            this.persistToken(DEV_BYPASS_TOKEN);
        }

        // Bootstrap is deliberately lazy. The first thing that needs a
        // session — the wizard's "Start" → ERP-Next click via
        // `UserSessionService.createSession()`, or any 401 from a gated
        // call via `SessionRetryInterceptor` — drives `bootstrapSession()`.
        //
        // Eagerly minting a session here would mean a brand-new user with a
        // valid token lands with `state.hasSession()` already truthy, which
        // the `onboardingWelcomeGuard` reads as "returning user, skip
        // welcome and resume the wizard". That deletes the Welcome step
        // from the first-run UX. Doing nothing here keeps Welcome reachable
        // and lets the wizard flow mint the session at the natural moment.
        if (this.state.hasSession()) {
            console.info('[plg-token] using stored session — first 401 will trigger re-bootstrap');
        } else if (this.getToken()) {
            console.info('[plg-token] token present, no session — bootstrap deferred to first session-required action');
        } else {
            console.info('[plg-token] no token at init — deferring sign-up prompt until a session is required');
        }
    }

    /**
     * Mints a new session bound to the stored `plg_token`. Concurrent callers
     * (e.g. multiple in-flight requests all hitting 401 at once) share the same
     * promise so the backend sees a single POST.
     *
     * On any failure the service has already invalidated local state and
     * raised `signUpRequired$` by the time the returned promise rejects —
     * callers don't need to perform additional cleanup.
     */
    bootstrapSession(): Promise<string> {
        if (this._bootstrapInFlight) {
            return this._bootstrapInFlight;
        }
        this._bootstrapInFlight = this.performBootstrap().finally(() => {
            this._bootstrapInFlight = null;
        });
        return this._bootstrapInFlight;
    }

    private async performBootstrap(): Promise<string> {
        const token = this.getToken();
        if (!token) {
            this.requireSignUp('plg_token missing');
            throw new Error('plg_token missing');
        }

        // Drop any stale session id before re-bootstrapping and notify
        // subscribers (e.g. UserSessionService.session$) that their cached
        // snapshot is no longer current. Onboarding state is preserved so a
        // silent re-bootstrap mid-wizard doesn't reset the user's progress.
        this.state.invalidateSession();

        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        const context = new HttpContext().set(SKIP_AUTH_RETRY, true);

        try {
            const res = await firstValueFrom(
                this.http.post<CreateSessionResponse>(`${environment.apiUrl}/api/v1/sessions`, null, {
                    headers,
                    context,
                }),
            );
            this.state.saveSessionId(res.session_id);
            this._contact$.next(res.contact);
            return res.session_id;
        } catch (err) {
            const status = (err as { status?: number })?.status;
            this.requireSignUp('http error', { status, error: err });
            throw err;
        }
    }

    /**
     * Bootstrap can't continue. Wipe local state and raise the sign-up signal
     * so the shell shows the in-app landing. Centralised so every failure
     * path (missing token, 401, 5xx, network error, second 401 from the retry
     * interceptor) leaves the browser in the same state, and the user always
     * sees the Sign Up CTA before any navigation away.
     */
    requireSignUp(reason: string, extra: Record<string, unknown> = {}): void {
        console.warn('[plg-token] sign-up required', { reason, ...extra });
        this.clearAll();
        this._signUpRequired$.next(true);
    }

    /** User-initiated bounce to the marketing site (Sign Up CTA on the landing). */
    goToMarketing(): void {
        window.open(environment.tryYourDataUrl, '_top');
    }

    getToken(): string | null {
        return localStorage.getItem(StorageKey.PlgToken);
    }

    /**
     * Wipes everything that ties this browser to a HubSpot contact. Routes
     * through `state.resetState()` so the onboarding state is cleared and
     * `sessionInvalidated$` fires alongside the session id wipe.
     */
    clearAll(): void {
        localStorage.removeItem(StorageKey.PlgToken);
        this.resetAnalyticsIdentity();
        this.state.resetState();
        this._contact$.next(null);
    }

    /**
     * A different `plg_token` arrived in the URL — drop everything that
     * belonged to the previous contact (session id, onboarding progress,
     * cached contact) before persisting the new token. Stops short of
     * removing the token from localStorage because the caller is about to
     * write the new one in immediately after. Also clears any stale
     * sign-up signal — the new contact gets a fresh chance to bootstrap.
     */
    private resetForNewContact(): void {
        this.resetAnalyticsIdentity();
        this.state.resetState();
        this._contact$.next(null);
        this._signUpRequired$.next(false);
    }

    /**
     * Reset the analytics identity/lifecycle keys when the contact goes away or
     * changes: drop the onboarding-started dedupe + duration stamp and the Mixpanel
     * identity/profile flags, and signal the analytics layer to clear Mixpanel's
     * persisted identity on next init (see `AnalyticsSdkService.init`) so a new
     * contact doesn't inherit the previous distinct_id.
     */
    private resetAnalyticsIdentity(): void {
        localStorage.removeItem(StorageKey.OnboardingStartedSent);
        localStorage.removeItem(StorageKey.OnboardingStartedAt);
        localStorage.removeItem(StorageKey.AnalyticsIdentified);
        localStorage.removeItem(StorageKey.AnalyticsProfileSet);
        localStorage.setItem(StorageKey.AnalyticsIdentityReset, '1');
    }

    private persistToken(token: string): void {
        localStorage.setItem(StorageKey.PlgToken, token);
    }

    private readUrlToken(): string | null {
        return new URLSearchParams(window.location.search).get('plg_token');
    }

    private stripUrlParam(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete('plg_token');
        window.history.replaceState({}, '', url.toString());
    }
}
